// src/binance/BitgetWalletDashboard.jsx (or src/bitget/WalletDashboard.jsx)
import React, { useState, useContext } from "react";
import { AuthContext } from "../authContext"; // adjust path if needed
import {
  useBitgetWallet,
  useBitgetPositions,
  useBitgetRealizedPnl,
  useBitgetOpenOrders,
  useBitgetPositionsWithTpsl,
} from "./useBitgetData"; // path as you prefer
import "../user/Dashboard.css";
import axios from "axios";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function BitgetWalletDashboard() {
  const { userId } = useContext(AuthContext) || {};
  const canClose = String(userId || localStorage.getItem("userId")) === "pziPDtOgJiX06oGuJxNwxE";
  const { wallet, isLoading: walletLoading } = useBitgetWallet();
  const { positions, isLoading: positionsLoading } = useBitgetPositionsWithTpsl();
  const { realizedPnl, isLoading: pnlLoading } = useBitgetRealizedPnl();
  const { orders, isLoading: ordersLoading } = useBitgetOpenOrders();

  const [sortField, setSortField] = useState("hour");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [invUsd, setInvUsd] = useState("");
  const [levX, setLevX] = useState("");
  const [calc, setCalc] = useState(null);
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";
  const [tradeUsd, setTradeUsd] = useState("50");
  const parseMoney = (s) => {
  const n = parseFloat(String(s ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
  };

  const parsePctAfterSlash = (s) => {
    // expects: "$7300.00 / 2.67%" OR "$7300.00 / -2.67%"
    const m = String(s ?? "").match(/\/\s*([+-]?\d+(\.\d+)?)%/);
    return m ? Number(m[1]) : null;
  };

    // Close-position modal state
  const [closeModal, setCloseModal] = useState({ open: false, symbol: null, side: null });

  function toBitgetApiSymbol(s) {
    let clean = String(s || "").trim().toUpperCase();
    clean = clean.replace(/\/+$/, "");               // strip trailing slashes
    if (clean.includes(":")) return clean;           // already like BTC/USDT:USDT

    if (clean.includes("/")) {                       // e.g. BTC/USDT
      return `${clean}:USDT`;
    }

    // e.g. BTCUSDT → BTC/USDT:USDT
    const m = clean.match(/^([A-Z0-9]+)(USDT)$/);
    if (m) return `${m[1]}/USDT:USDT`;

    // last resort: assume base is given → map to USDT swap
    return `${clean}/USDT:USDT`;
  }

 const openCloseModal = (pos) => {
   if (!canClose || pos.type !== "Futures") return;
   setCloseModal({ open: true, symbol: pos.symbol, side: pos.side });
 };

  const cancelClose = () => setCloseModal({ open: false, symbol: null, side: null });

  const confirmClose = async () => {
    try {
      if (!canClose) {
        alert("Closing positions is restricted.");
        return;
      }
      const token = localStorage.getItem("token");
      const symbol = toBitgetApiSymbol(closeModal.symbol);
      const side   = String(closeModal.side || "").toUpperCase(); // "LONG" | "SHORT"

      await axios.post(
        `${baseUrl}/api/bitget/bot/close-position`,
        { symbol, side },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      setCloseModal({ open: false, symbol: null, side: null });
      window.location.reload(); // simple refresh to reflect the change
    } catch (e) {
      alert(`Close failed: ${e?.response?.data?.error || e.message}`);
    }
  };


   const isLoading = walletLoading || positionsLoading || pnlLoading || ordersLoading;
   
    if (isLoading || !wallet) {
      return (
        <div className="loading-modal">
          <div className="loading-content">
            <div className="loader" />
            <p>Loading wallet data...</p>
          </div>
        </div>
      );
    }

    function filterTradesByDate(history, fromDate, toDate) {
      const fromTs = fromDate ? new Date(`${fromDate}T00:00:00Z`).getTime() : null;
      const toTs   = toDate   ? new Date(`${toDate}T23:59:59.999Z`).getTime() : null;

      return (history || []).filter(trade => {
        const t = Number(trade.time);
        const ms = t < 1e12 ? t * 1000 : t; // handle seconds vs ms
        if (Number.isNaN(ms)) return false;
        if (fromTs && ms < fromTs) return false;
        if (toTs && ms > toTs) return false;
        return true;
      });
    }

    function extractUnleveredPercent(t) {
        const cand = t.profitRate ?? t.pnlPercent ?? t.pnlRate ?? t.rate ?? t.roi ?? null;

        if (cand != null && !Number.isNaN(Number(cand))) {
          const n = Number(cand);
          // Heuristic: if in [ -1 .. 1 ] assume it's a fraction → convert to %
          return Math.abs(n) <= 1 ? n * 100 : n;
        }

        // Derive from income / notional if we have enough fields.
        // Try a few likely shapes:
        const income = Number(t.income);
        if (!Number.isFinite(income)) return null;

        // Possible notional/margin/size combos:
        const qty      = Number(t.qty ?? t.amount ?? t.size ?? t.vol ?? t.volume);
        const avgPrice = Number(t.avgPrice ?? t.price ?? t.fillPrice ?? t.execPrice ?? t.entryPrice);
        const notional = Number(t.notional ?? t.value ?? (Number.isFinite(qty) && Number.isFinite(avgPrice) ? qty * avgPrice : NaN));
        const margin   = Number(t.margin ?? t.marginAmount);

        // Prefer true notional (qty*price). If missing, fall back to margin.
        let base = Number.isFinite(notional) && notional > 0 ? notional
                : (Number.isFinite(margin)   && margin   > 0 ? margin : NaN);

        if (!Number.isFinite(base) || base <= 0) return null;

        // % = income / base * 100 (unlevered if base is actual notional at entry)
        return (income / base) * 100;
      }


    function handleCalculate() {
      const filtered = filterTradesByDate(realizedPnl?.history || [], fromDate, toDate);

      // Gather percent for each trade (skip ones we can't resolve)
      const percents = filtered
        .map(extractUnleveredPercent)
        .filter(p => Number.isFinite(p));

      // If we couldn't resolve per-trade %, gracefully fall back to your old baseline
      // so the button never feels "broken".
      const fallbackTotalIncome = filtered.reduce((s, t) => s + Number(t?.income || 0), 0);
      const hasAnyPct = percents.length > 0;

      // Sum of simple percents (no compounding, per your instruction "do nothing else")
      const sumPct = hasAnyPct ? percents.reduce((a, b) => a + b, 0) : null;

      const investment = Number(invUsd) || 0;
      const leverage   = Number(levX)   || 1;

      let scaledPnl, mode;
      if (hasAnyPct) {
        // Percent mode
        scaledPnl = investment * leverage * (sumPct / 100);
        mode = "percent";
      } else {
        // Fallback mode: legacy baseline of 1000 (keeps old behavior alive)
        const BASE = 1000;
        scaledPnl = investment * leverage * (fallbackTotalIncome / BASE);
        mode = "fallback-1000";
      }

      setCalc({
        tradeCount: filtered.length,
        usedPercents: percents.length,
        sumPct: hasAnyPct ? sumPct : null,
        totalIncome: fallbackTotalIncome, // for reference
        investment,
        leverage,
        mode,
        scaledPnl
      });
    }

    function fmtSign(n, digits = 2) {
  if (!Number.isFinite(n)) return "—";
  const s = n.toFixed(digits);
  return (n > 0 ? "+" : "") + s;
}

function calcProjected(pos) {
  const { isLong, amountBase, entryPrice, markPrice, tp, sl } = pos;

  if (!Number.isFinite(amountBase) || amountBase <= 0 || !Number.isFinite(entryPrice) || entryPrice <= 0) {
    return {
      pctNow: null, usdNow: null,
      pctToTp: null, usdAtTp: null,
      pctToSl: null, usdAtSl: null,
      rr: null,
      progToTp: null, progToSl: null,
    };
  }

  const pctTo = (target) => {
    if (!Number.isFinite(target) || target <= 0) return null;
    const raw = isLong ? (target - entryPrice) / entryPrice : (entryPrice - target) / entryPrice;
    return raw * 100;
  };

  // Use *base* quantity (amount in coin units). If your positions are in contracts,
  // amountBase already includes contractSize.
  const usdTo = (target) => {
    if (!Number.isFinite(target)) return null;
    const delta = (target - entryPrice) * amountBase * (isLong ? 1 : -1);
    return delta;
  };

  const pctNow = pctTo(markPrice);
  const usdNow = usdTo(markPrice);

  const pctToTp = pctTo(tp);
  const usdAtTp = usdTo(tp);

  const pctToSl = pctTo(sl);
  const usdAtSl = usdTo(sl);

  let rr = null;
  if (Number.isFinite(usdAtTp) && Number.isFinite(usdAtSl)) {
    const reward = Math.abs(usdAtTp);
    const risk   = Math.abs(usdAtSl);
    rr = risk > 0 ? reward / risk : null;
  }

  const clamp01 = (x) => Math.max(0, Math.min(1, x));
  let progToTp = null, progToSl = null;

  if (Number.isFinite(tp)) {
    const num = isLong ? (markPrice - entryPrice) : (entryPrice - markPrice);
    const den = isLong ? (tp - entryPrice)       : (entryPrice - tp);
    progToTp = Number.isFinite(den) && den > 0 ? clamp01(num / den) : null;
  }
  if (Number.isFinite(sl)) {
    const num = isLong ? (entryPrice - markPrice) : (markPrice - entryPrice);
    const den = isLong ? (entryPrice - sl)        : (sl - entryPrice);
    progToSl = Number.isFinite(den) && den > 0 ? clamp01(num / den) : null;
  }

  return { pctNow, usdNow, pctToTp, usdAtTp, pctToSl, usdAtSl, rr, progToTp, progToSl };
}


  // ---- Spot holdings (if you return spot from backend; otherwise omit) ----
  const spotEntries = Object.entries(wallet.spot || {});
  const spotHoldings = spotEntries
    .map(([symbol, value]) => {
      const amount = parseFloat(value?.free || value?.available || 0);
      const price = (symbol === "USDT" ? 1 : wallet.prices?.[`${symbol}USDT`]) || 0;
      const total = amount * price;
      return {
        symbol,
        type: "Spot",
        amount,
        entryPrice: null,
        markPrice: price,
        pnl: null,
        total,
      };
    })
    .filter(({ total }) => total > 10);
  const spotTotalValue = spotHoldings.reduce((a, c) => a + c.total, 0);

  // ---- Futures holdings (Bitget) ----
const futuresHoldings = (positions || [])
  .filter(p => Number(p?.contracts || p?.contractsSize || p?.positionAmt || 0) !== 0)
  .map(p => {
    const symbol = (p.symbol || "").replace("USDT:USDT", "").replace("/USDT", "");

    // Contracts vs base-coin amount
    const rawContracts = Number(p.contracts ?? p.contractsSize ?? p.positionAmt ?? 0); // may be contracts or base units depending on source
    const contractSize = Number(
      p.contractSize ??
      p.info?.contractSize ??
      p.info?.size ??
      1
    ); // e.g., 0.001 BTC per contract

    // amountBase = how many *coins* your position represents
    const amountBase = Math.abs(rawContracts) * contractSize;

    const entryPrice = Number(p.entryPrice ?? p.info?.avgPrice ?? 0);
    const markPrice  = Number(p.markPrice ?? p.info?.markPrice ?? p.info?.last ?? 0);

    // Unrealized PnL: prefer backend/exchange calc; if missing, compute from base qty
    let pnl = Number(p.unrealizedPnl ?? p.unRealizedProfit ?? p.info?.unrealizedPL);
    if (!Number.isFinite(pnl)) {
      if (Number.isFinite(entryPrice) && Number.isFinite(markPrice)) {
        const sideLong = rawContracts >= 0; // sign before abs
        pnl = (markPrice - entryPrice) * amountBase * (sideLong ? 1 : -1);
      } else {
        pnl = 0;
      }
    }

    const tp = Number(p.tp ?? p.info?.takeProfit ?? NaN);
    const sl = Number(p.sl ?? p.info?.stopLoss   ?? NaN);

    const side = p.side
      ? String(p.side).toUpperCase()
      : (rawContracts >= 0 ? "LONG" : "SHORT");

    // Notional & percent
    const total = Number.isFinite(markPrice) ? amountBase * markPrice : 0;
    const baseNotionalAtEntry = Number.isFinite(entryPrice) ? amountBase * entryPrice : 0;
    const percentPnl = (baseNotionalAtEntry > 0)
      ? (pnl / baseNotionalAtEntry) * 100
      : 0;

    return {
      symbol,
      type: "Futures",
      side,
      isLong: side === "LONG",
      // keep original fields
      amount: Math.abs(rawContracts),     // contracts (display)
      entryPrice: Number.isFinite(entryPrice) ? entryPrice : 0,
      markPrice: Number.isFinite(markPrice) ? markPrice : 0,
      pnl,
      tp: Number.isFinite(tp) ? tp : null,
      sl: Number.isFinite(sl) ? sl : null,
      percentPnl,
      total,

      // NEW for projections:
      contractSize: Number.isFinite(contractSize) ? contractSize : 1,
      amountBase, // coins (amount * contractSize)
    };
  });


  const allPositions = [...spotHoldings, ...futuresHoldings];

  // Running (unrealized) PnL
  const totalUnrealizedPnl = futuresHoldings.reduce((s, p) => s + (p.pnl || 0), 0);
  const totalEntryNotional = futuresHoldings.reduce((s, p) => {
    const entry = Number(p.entryPrice) || 0;
    const qty   = Number(p.amountBase) || 0;
    return s + (entry * qty);
  }, 0);
  const runningPnlPct = totalEntryNotional > 0 ? (totalUnrealizedPnl / totalEntryNotional) * 100 : 0;

// inside BitgetWalletDashboard component, replace the grouping block
let groupedTrades = {};
let customTp = 0, customSl = 0;

if (realizedPnl && Array.isArray(realizedPnl.history)) {
  // parse date inputs (YYYY-MM-DD from <input type="date">)
  const fromTs = fromDate ? new Date(`${fromDate}T00:00:00Z`).getTime() : null;
  const toTs   = toDate   ? new Date(`${toDate}T23:59:59.999Z`).getTime() : null;

  // 1) pre-filter counts for debugging
  const preCount = realizedPnl.history.length;

  const filtered = realizedPnl.history.filter(trade => {
    const t = Number(trade.time);
    const ms = t < 1e12 ? t * 1000 : t; // seconds → ms if needed
    if (Number.isNaN(ms)) return false;
    if (fromTs && ms < fromTs) return false;
    if (toTs && ms > toTs) return false;
    return true;
  });

  console.log('[Bitget][closed] preCount=', preCount, 'after date filter=', filtered.length);

  groupedTrades = filtered.reduce((acc, t) => {
    const ts = Number(t.time);
    const ms = ts < 1e12 ? ts * 1000 : ts;
    const d = new Date(ms);
    const hourKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')} ${String(d.getUTCHours()).padStart(2,'0')}:00`;
    const sym = t.symbol || 'USDT';
    const key = `${sym}_${hourKey}`;
    if (!acc[key]) acc[key] = { symbol: sym, hour: hourKey, income: 0, count: 0, types: []  };
    acc[key].income += Number(t.income || 0);
    acc[key].count += 1;
    acc[key].types.push(t.businessType);
    return acc;
  }, {});

  // Wins/Losses
  customTp = 0; customSl = 0;
  Object.values(groupedTrades).forEach(g => (g.income > 0 ? customTp++ : customSl++));

  console.log('[Bitget][closed] groups=', Object.keys(groupedTrades).length,
              'sample=', Object.values(groupedTrades)[0]);
}


  // Sort + paginate groupedTrades:
  const groupedTradeList = Object.values(groupedTrades);

  const sortedTrades = groupedTradeList.sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    // Convert date string to timestamp for sorting by hour
    if (sortField === "hour") {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    }

    if (valA < valB) return sortDirection === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedTrades = sortedTrades.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

    // Add this helper to toggle sorting:
  function handleSort(field) {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

    const handleExportPdf = () => {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

      // ✅ same theme as Binance final
      const PDF_THEME = {
        charcoal: [14, 18, 28],
        white: [255, 255, 255],

        card: [246, 249, 255],
        cardAlt: [238, 244, 255],
        keyRow: [228, 238, 255],
        line: [190, 205, 230],

        ink: [16, 22, 34],
        label: [88, 102, 128],
        value: [20, 28, 44],
        muted: [120, 135, 160],

        green: [0, 170, 120],
        red: [220, 60, 85],
      };

      const TABLE_BASE = {
        theme: "grid",
        styles: {
          font: "helvetica",
          fontSize: 10,
          cellPadding: 8,
          lineColor: PDF_THEME.line,
          lineWidth: 0.7,
          valign: "middle",
        },
        headStyles: {
          fillColor: PDF_THEME.charcoal,
          textColor: PDF_THEME.white,
          fontStyle: "bold",
          fontSize: 11,
          halign: "center",
          cellPadding: 9,
        },
        bodyStyles: {
          fillColor: PDF_THEME.card,
          textColor: PDF_THEME.ink,
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: PDF_THEME.cardAlt,
        },
        margin: { left: 40, right: 40 },
      };

      // FILTER closed trades based on from/to dates (handle seconds vs ms)
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate + "T23:59:59") : null;

      const filteredHistory = (realizedPnl?.history || []).filter((trade) => {
        const tNum = Number(trade.time);
        const ms = tNum < 1e12 ? tNum * 1000 : tNum;
        const tradeDate = new Date(ms);
        return (!from || tradeDate >= from) && (!to || tradeDate <= to);
      });

      const filteredGroupedTrades = filteredHistory.reduce((acc, trade) => {
        const symbol = trade.symbol;
        const tNum2 = Number(trade.time);
        const ms2 = tNum2 < 1e12 ? tNum2 * 1000 : tNum2;
        const date = new Date(ms2);

        const hourKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:00`;
        const key = `${symbol}_${hourKey}`;

        if (!acc[key]) {
          acc[key] = { symbol, hour: hourKey, income: 0, count: 0 };
        }
        acc[key].income += Number(trade.income || 0);
        acc[key].count += 1;
        return acc;
      }, {});

      // --- PDF header (black bar, no radius) ---
      doc.setCharSpace(0.5);
      doc.setFontSize(18);
      doc.setTextColor(...PDF_THEME.white);
      doc.setFillColor(...PDF_THEME.charcoal);
      doc.rect(40, 38, 515, 36, "F");
      doc.text("TrendScout Trading Summary Report", 55, 63);

      // ---- Total value & realized PNL for Bitget ----
      const accountEquity = Number(wallet.swap?.accountEquity || 0);
      const totalValue = (accountEquity + spotTotalValue).toFixed(2);

      const baseCapital = Number(wallet.bitfamTradingAmount || 0);
      const currentEquity = accountEquity + spotTotalValue;
      const realizedPnlValue = baseCapital ? currentEquity - baseCapital : 0;

      const summaryBody = [
        ["Total Funds", `$${totalValue}`],
        ["Spot", `$${spotTotalValue.toFixed(2)}`],
        ["Futures (Equity)", `$${accountEquity.toFixed(2)}`],
        ["Realized PNL", `$${realizedPnlValue.toFixed(2)}${realizedPnlValue >= 0 ? " (Gain)" : " (Loss)"}`],
        ["Win Rate", `${((customTp / (customTp + customSl || 1)) * 100).toFixed(2)}%`],
      ];

      // ---- Filtered stats ----
      let filteredWins = 0;
      let filteredLosses = 0;
      let filteredTotalPnl = 0;

      Object.values(filteredGroupedTrades).forEach((g) => {
        filteredTotalPnl += Number(g.income || 0);
        if (g.income > 0) filteredWins++;
        else filteredLosses++;
      });

      const filteredTradeCount = filteredWins + filteredLosses;
      const filteredWinRate =
        filteredTradeCount > 0 ? ((filteredWins / filteredTradeCount) * 100).toFixed(2) : "0.00";

      // ✅ trade-size based ROI calcs (same as Binance)
      const perTradeUsd = Math.max(0, Number(tradeUsd) || 0);

      // A) simple capital = $perTrade * trades
      const totalUsedA = perTradeUsd * filteredTradeCount;
      const roiA = totalUsedA > 0 ? (filteredTotalPnl / totalUsedA) * 100 : 0;

      // B) effective capital with recycling every 20 hours
      const AVG_OUTCOME_HRS = 20;

      let windowHours = 0;
      if (from && to) {
        windowHours = Math.max(0, (to.getTime() - from.getTime()) / (1000 * 60 * 60));
      } else {
        const times = filteredHistory
          .map((t) => {
            const n = Number(t.time);
            const ms = n < 1e12 ? n * 1000 : n;
            return ms;
          })
          .filter(Number.isFinite)
          .sort((a, b) => a - b);
        if (times.length >= 2) {
          windowHours = Math.max(0, (times[times.length - 1] - times[0]) / (1000 * 60 * 60));
        }
      }

      const cycles = Math.max(1, Math.ceil(windowHours / AVG_OUTCOME_HRS));
      const peakConcurrentTrades = Math.max(1, Math.ceil(filteredTradeCount / cycles));
      const effectiveUsedB = perTradeUsd * peakConcurrentTrades;
      const roiB = effectiveUsedB > 0 ? (filteredTotalPnl / effectiveUsedB) * 100 : 0;

      // ---- Total Data table (with Realized PNL colored) ----
      autoTable(doc, {
        ...TABLE_BASE,
        startY: 90,
        head: [["Total Data", "Value"]],
        body: summaryBody,
        didParseCell(data) {
          if (data.section !== "body") return;
          const label = String(data.row?.raw?.[0] || "");

          if (data.column.index === 0) {
            data.cell.styles.textColor = PDF_THEME.label;
            return;
          }

          if (data.column.index === 1) {
            data.cell.styles.font = "courier";
            data.cell.styles.textColor = PDF_THEME.value;

            if (label === "Realized PNL") {
              const v = parseMoney(String(data.cell?.raw || ""));
              if (v != null) {
                data.cell.styles.textColor = v < 0 ? PDF_THEME.red : PDF_THEME.green;
                data.cell.styles.fontStyle = "bold";
              }
            }
          }
        },
      });

      // ---- Filtered Data table (key rows + colored PNL/returns) ----
      autoTable(doc, {
        ...TABLE_BASE,
        startY: doc.lastAutoTable.finalY + 16,
        head: [["Filtered Data", "Value"]],
        body: [
          ["Filtered PNL", `$${filteredTotalPnl.toFixed(2)}`],
          ["Filtered Win Rate", `${filteredWinRate}%`],
          ["Amount/Trade", `$${perTradeUsd.toFixed(0)} / ${filteredTradeCount} trades`],
          ["Capital Used (per-trade)", `$${totalUsedA.toFixed(2)} / ${roiA.toFixed(2)}%`],
          ["Filter Window", `${windowHours.toFixed(1)} hrs (~${(windowHours / 24).toFixed(2)} days)`],
          ["Capital Used (recycled)", `$${effectiveUsedB.toFixed(2)} / ${roiB.toFixed(2)}%`],
        ],
        didParseCell(data) {
          if (data.section !== "body") return;

          const label = String(data.row?.raw?.[0] || "");
          const value = String(data.cell?.raw || "");

          const key = new Set(["Filtered PNL", "Capital Used (per-trade)", "Capital Used (recycled)"]);

          if (key.has(label)) data.cell.styles.fillColor = PDF_THEME.keyRow;

          if (data.column.index === 0) {
            data.cell.styles.textColor = PDF_THEME.label;
            if (key.has(label)) data.cell.styles.fontStyle = "bold";
            return;
          }

          if (data.column.index === 1) {
            data.cell.styles.font = "courier";
            data.cell.styles.textColor = PDF_THEME.value;

            if (label === "Filtered PNL") {
              const v = parseMoney(value);
              if (v != null) {
                data.cell.styles.textColor = v < 0 ? PDF_THEME.red : PDF_THEME.green;
                data.cell.styles.fontStyle = "bold";
              }
              return;
            }

            if (label === "Capital Used (per-trade)" || label === "Capital Used (recycled)") {
              const pct = parsePctAfterSlash(value);
              if (pct != null) {
                data.cell.styles.textColor = pct < 0 ? PDF_THEME.red : PDF_THEME.green;
                data.cell.styles.fontStyle = "bold";
              }
            }
          }
        },
      });

      // ---- Holdings table (color PNL column) ----
      autoTable(doc, {
        ...TABLE_BASE,
        startY: doc.lastAutoTable.finalY + 26,
        head: [["Open", "Type", "Amount", "Entry", "Mark/Spot", "PNL", "Value"]],
        body: allPositions.map((pos) => [
          pos.symbol,
          pos.type,
          pos.amount?.toFixed?.(0) ?? String(pos.amount ?? "-"),
          pos.entryPrice != null ? `$${Number(pos.entryPrice).toFixed(4)}` : "-",
          pos.markPrice != null ? `$${Number(pos.markPrice).toFixed(4)}` : "-",
          typeof pos.pnl === "number" ? `$${pos.pnl.toFixed(2)} (${pos.percentPnl?.toFixed(2)}%)` : "-",
          pos.total != null ? `$${Number(pos.total).toFixed(2)}` : "-",
        ]),
        didParseCell(data) {
          // PNL column index = 5
          if (data.section === "body" && data.column.index === 5) {
            const v = parseMoney(data.cell.raw);
            if (v != null) {
              data.cell.styles.textColor = v < 0 ? PDF_THEME.red : PDF_THEME.green;
              data.cell.styles.fontStyle = "bold";
            }
          }
        },
      });

      // ---- Closed rows (sorted newest first + numbering) ----
      const toMs = (h) => {
        const [ymd, hm] = String(h || "").split(" ");
        const [Y, M, D] = (ymd || "").split("-").map(Number);
        const H = Number(String(hm || "0").split(":")[0]);
        return Date.UTC(Y || 1970, (M || 1) - 1, D || 1, H || 0, 0, 0);
      };

      const closedRows = Object.values(filteredGroupedTrades).sort((a, b) => toMs(b.hour) - toMs(a.hour));

      autoTable(doc, {
        ...TABLE_BASE,
        startY: doc.lastAutoTable.finalY + 26,
        head: [["#", "Closed", "Hour", "Income (USDT)", "Income %"]],
        body: closedRows.map((t, i) => [
          i + 1,
          t.symbol,
          t.hour,
          `$${Number(t.income || 0).toFixed(2)}`,
          `${((Number(t.income || 0) / 1000) * 100).toFixed(2)}%`,
        ]),
        didParseCell(data) {
          // Income column index 3
          if (data.section === "body" && data.column.index === 3) {
            const v = parseMoney(data.cell.raw);
            if (v != null) {
              data.cell.styles.textColor = v < 0 ? PDF_THEME.red : PDF_THEME.green;
              data.cell.styles.fontStyle = "bold";
            }
          }
        },
      });

      // ---- Footer (generated + page numbers) ----
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...PDF_THEME.muted);

        const footerY = doc.internal.pageSize.getHeight() - 18;
        doc.text(`Generated: ${new Date().toLocaleString()}`, 40, footerY);

        const rightX = doc.internal.pageSize.getWidth() - 40;
        doc.text(`Page ${i} / ${pageCount}`, rightX, footerY, { align: "right" });
      }

      doc.save(`TrendScout_Trading_Report_${new Date().toISOString().split("T")[0]}.pdf`);
    };

  
    function getClosedSide(types = []) {

      const t = (types || []).map(x => String(x || '').toLowerCase());

      if (t.some(v => v.includes('close_long')))  return 'LONG';
      if (t.some(v => v.includes('close_short'))) return 'SHORT';

      if (t.includes('sell')) return 'LONG';
      if (t.includes('buy'))  return 'SHORT';

      return '-';
    }


  return (
    <div className="dashboard-main">
      <div className="outcome-table-wrapper-dashboard">
        <div className="outcome-table">
          <div className="expanded-content">
            <div className="expanded-section">
              <h3>💸 Funds (Bitget)</h3>
              <p><strong>Total:</strong> ${ (Number(wallet.swap?.accountEquity || 0) + spotTotalValue).toFixed(0) }</p>
              <p><strong>Spot:</strong> ${ spotTotalValue.toFixed(0) }</p>
              <p><strong>Futures:</strong> ${ Number(wallet.swap?.accountEquity || 0).toFixed(0) }</p>
              {(() => {
                  const totalEquity = Number(wallet.swap?.accountEquity || 0) + spotTotalValue;
                  const baseCapital = Number(wallet.bitfamTradingAmount || 0);
                  const pnl = baseCapital ? totalEquity - baseCapital : 0;
                  return (
                    <p>
                      <strong>Realized PNL:</strong>{" "}
                      <span style={{ color: pnl >= 0 ? "lime" : "red" }}>
                        ${pnl.toFixed(0)}
                      </span>
                    </p>
                  );
                })()}
            </div>

            <div className="expanded-section">
              <h3>📊 Performance Summary</h3>
              <p><strong>Total Trades:</strong> {customTp + customSl}</p>
              <p><strong>Wins:</strong> {customTp}</p>
              <p><strong>Losses:</strong> {customSl}</p>
              <p><strong>Win Rate:</strong> {((customTp / (customTp + customSl || 1)) * 100).toFixed(2)}%</p>
              <p>
                <strong>Running PnL:</strong>{" "}
                <span style={{ color: totalUnrealizedPnl >= 0 ? "lime" : "red", fontWeight: 700 }}>
                  ${totalUnrealizedPnl.toFixed(0)} ({runningPnlPct.toFixed(2)}%)
                </span>
              </p>
              <label style={{ display: "block", marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                Amount per trade ($):
              </label>
              <input
                className="profit-input"
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 50"
                value={tradeUsd}
                onChange={(e) => setTradeUsd(e.target.value)}
              />
              <button onClick={handleExportPdf} className="trade-export-button" style={{ marginTop: '15px' }}>
                📄 Export to PDF
              </button>
            </div>

            <div className="expanded-section">
              <h3>💸 Filter</h3>
              <div>
                <label>From:</label>
                <input className="date-input-wallet" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
              </div>
              <div>
                <label>To:</label>
                <input className="date-input-wallet" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
              </div>
            </div>
            
            <div className="expanded-section">
                <div className="profit-card-header">
                  <span role="img" aria-label="chart">📈</span>&nbsp; Profit Calculator
                </div>

                <label className="profit-label">Investment ($):</label>
                <input
                  className="profit-input"
                  type="number"
                  placeholder="e.g. 1000"
                  value={invUsd}
                  onChange={(e) => setInvUsd(e.target.value)}
                />

                <label className="profit-label">Leverage (x):</label>
                <input
                  className="profit-input"
                  type="number"
                  placeholder="e.g. 3"
                  value={levX}
                  onChange={(e) => setLevX(e.target.value)}
                />

                <button className="profit-btn" style={{ marginTop: '15px' }} onClick={handleCalculate}>Calculate</button>

                {calc && (
                  <div className="profit-result">
                    <div><strong>Closed trades used:</strong> {calc.tradeCount}</div>
                    <div>
                      <strong>Hypothetical PnL:</strong>{" "}
                      <span style={{ color: calc.scaledPnl >= 0 ? "lime" : "red", fontWeight: 700 }}>
                        ${calc.scaledPnl.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

          </div>

          <div className="outcome-table">
            <div className="table-scroll-container">
              <table>
                <thead>
                  <tr>
                    <th>Holdings</th>
                    <th>Side</th>
                    <th>Value</th>
                    <th>Entry Price</th>
                    <th>Mark Price</th>
                    <th>TP</th>
                    <th>SL</th>
                    <th>PNL</th>
                    <th>→ TP PnL</th>
                    <th>→ SL PnL</th>
                    <th>R:R</th>
                  </tr>
                </thead>
                <tbody>
                  {allPositions.map((pos, idx) => {
                    const proj = pos.type === "Futures" ? calcProjected(pos) : {};
                    return (
                      <tr
                        key={idx}
                        onClick={canClose && pos.type === "Futures" ? () => openCloseModal(pos) : undefined}
                        style={{
                          cursor: canClose && pos.type === "Futures" ? "pointer" : "default",
                          opacity: !canClose && pos.type === "Futures" ? 0.7 : 1
                        }}
                        title={canClose && pos.type === "Futures" ? "Click to close position" : "" }
                      >
                      <td>{pos.symbol}</td>
                      <td>
                        {pos.type === "Futures" ? (
                          <span
                            className="side-badge"
                            style={{
                              background: pos.isLong ? "rgba(0,255,0,0.12)" : "rgba(255,0,0,0.12)",
                              color: pos.isLong ? "lime" : "red",
                              border: `1px solid ${pos.isLong ? "rgba(0,255,0,0.35)" : "rgba(255,0,0,0.35)"}`
                            }}
                          >
                            {pos.side}
                          </span>
                        ) : "—"}
                      </td>
                      <td>${pos.total ? pos.total.toFixed(0) : "-"}</td>
                      <td>${pos.entryPrice ? pos.entryPrice.toFixed(4) : "-"}</td>
                      <td>${pos.markPrice ? pos.markPrice.toFixed(4) : "-"}</td>
                      <td>{pos.tp ? pos.tp.toFixed(4) : "—"}</td>
                      <td>{pos.sl ? pos.sl.toFixed(4) : "—"}</td>
                      <td style={{ color: pos.pnl > 0 ? "lime" : pos.pnl < 0 ? "red" : "white", whiteSpace: "nowrap" }}>
                        {typeof pos.pnl === "number" ? `$${pos.pnl.toFixed(0)}` : "-"}
                        {" "}({typeof pos.percentPnl === "number" ? `${pos.percentPnl.toFixed(2)}%` : "-"})
                      </td>
                            {/* → TP projection */}
                      <td style={{ whiteSpace: "nowrap", color: proj?.usdAtTp > 0 ? "lime" : proj?.usdAtTp < 0 ? "red" : "white" }}>
                        {Number.isFinite(proj?.usdAtTp)
                          ? `$${fmtSign(proj.usdAtTp, 0)} (${fmtSign(proj.pctToTp, 2)}%)`
                          : "—"}
                        {Number.isFinite(proj?.progToTp) && (
                          <div style={{ width: 80, height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 6, overflow: "hidden", display: "inline-block", marginLeft: 6, verticalAlign: "middle" }}>
                            <div style={{ width: `${(proj.progToTp * 100).toFixed(0)}%`, height: "100%", background: "rgba(0,255,0,0.5)" }} />
                          </div>
                        )}
                      </td>

                      {/* → SL projection */}
                      <td style={{ whiteSpace: "nowrap", color: proj?.usdAtSl > 0 ? "lime" : proj?.usdAtSl < 0 ? "red" : "white" }}>
                        {Number.isFinite(proj?.usdAtSl)
                          ? `$${fmtSign(proj.usdAtSl, 0)} (${fmtSign(proj.pctToSl, 2)}%)`
                          : "—"}
                        {Number.isFinite(proj?.progToSl) && (
                          <div style={{ width: 80, height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 6, overflow: "hidden", display: "inline-block", marginLeft: 6, verticalAlign: "middle" }}>
                            <div style={{ width: `${(proj.progToSl * 100).toFixed(0)}%`, height: "100%", background: "rgba(255,0,0,0.5)" }} />
                          </div>
                        )}
                      </td>

                      {/* R:R */}
                      <td style={{ fontWeight: 700 }}>
                        {Number.isFinite(proj?.rr) ? proj.rr.toFixed(2) : "—"}
                      </td>
                    </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

            {orders && orders.length > 0 && (
              <div className="outcome-table">
                <div className="table-scroll-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Pending</th>
                        <th>Side</th>
                        <th className="hide">Amount</th>
                        <th>Price</th>
                        <th>TP</th> 
                        <th>SL</th> 
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o, idx) => (
                        <tr key={idx}>
                          <td>{new Date(o.timestamp).toLocaleString()}</td>
                          <td>{o.symbol}</td>
                          <td>
                            <span
                              className="side-badge"
                              style={{
                                background: o.side === "buy" ? "rgba(0,255,0,0.12)" : "rgba(255,0,0,0.12)",
                                color: o.side === "buy" ? "lime" : "red",
                                border: `1px solid ${o.side === "buy" ? "rgba(0,255,0,0.35)" : "rgba(255,0,0,0.35)"}`,
                              }}
                            >
                              {o.side.toUpperCase()}
                            </span>
                          </td>
                         
                          <td className="hide">{o.amount}</td>
                          <td>{o.price || "-"}</td>
                          <td>{o.tp ? `$${o.tp}` : "-"}</td>  
                          <td>{o.sl ? `$${o.sl}` : "-"}</td> 
                          <td>{o.status || "OPEN"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}



          {realizedPnl && Array.isArray(realizedPnl.history) && (
            <div className="outcome-table">
                          {fromDate && toDate && (
                    <p style={{ marginBottom: "10px" }}>
                        Showing trades from <strong>{fromDate}</strong> to <strong>{toDate}</strong>
                    </p>
                    )}
              <div className="table-scroll-container">
                {Object.keys(groupedTrades).length === 0 && (
                  <div style={{ padding: 12, opacity: 0.8 }}>
                    No closed trades found for the selected period.
                    {(!fromDate && !toDate) && " Try expanding the days on backend or check filters."}
                  </div>
                )}
                <table>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("hour")}>Time</th>
                      <th onClick={() => handleSort("symbol")}>Closed</th>
                      <th>Side</th>
                      
                      <th onClick={() => handleSort("income")}>
                        PnL
                      </th>
                      <th onClick={() => handleSort("count")}>PnL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTrades.map((group, idx) => (
                      <tr key={idx}>
                        <td>{group.hour}</td>
                        <td>{group.symbol}</td>
                          <td>
                            {(() => {
                              const side = getClosedSide(group.types || []);
                              if (side === "LONG" || side === "SHORT") {
                                const isLong = side === "LONG";
                                return (
                                  <span
                                    className="side-badge"
                                    style={{
                                      background: isLong ? "rgba(0,255,0,0.12)" : "rgba(255,0,0,0.12)",
                                      color: isLong ? "lime" : "red",
                                      border: `1px solid ${isLong ? "rgba(0,255,0,0.35)" : "rgba(255,0,0,0.35)"}`,
                                    }}
                                  >
                                    {side}
                                  </span>
                                );
                              }
                              return "—";
                            })()}
                          </td>   
                        <td
                          style={{ color: group.income >= 0 ? "lime" : "red", fontWeight: "bold" }}
                        >
                          ${group.income.toFixed(0)}
                        </td>
                        <td>{(group.income / 1000 * 100).toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                <div className="wallet-table-center">
                  <div
                    style={{ marginTop: "10px", display: "flex", gap: "10px" }}
                  >
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    >
                      Prev
                    </button>
                    <span>
                      Page {currentPage} /{" "}
                      {Math.ceil(groupedTradeList.length / itemsPerPage)}
                    </span>
                    <button
                      disabled={
                        currentPage ===
                        Math.ceil(groupedTradeList.length / itemsPerPage)
                      }
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {closeModal.open && (
        <div className="modal-overlay" onClick={cancelClose}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Close Position</h3>
            <p>
              Are you sure you want to close <strong>{closeModal.side}</strong> on{" "}
              <strong>{closeModal.symbol}</strong>?
            </p>
            <div className="modal-actions">
              <button onClick={cancelClose}>Cancel</button>
                <button className="danger" onClick={confirmClose} disabled={!canClose}>
                  CLOSE POSITION
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

